import { Menu, Portal, useDisclosure } from '@chakra-ui/react';
import { QuoteStatusEnum, QuoteWithPdfFields } from '@monetize/types/app';
import { MimeTypeEnum } from '@monetize/types/common';
import JSZip from 'jszip';
import { type MouseEvent, useState } from 'react';
import { MdChevronRight, MdOutlineFileDownload } from 'react-icons/md';
import { handleApiErrorToast } from '../../api/axios';
import {
  doGetQuoteDocumentConfigDetails,
  doGetQuoteStorageById,
  doPrintQuoteToDocx,
  doPrintQuoteToPdf,
  useGetAttachedQuoteDocuments,
} from '../../api/cpqService';
import { useFlags } from '../../services/launchDarkly';
import {
  addUniqueZipFile,
  base64ToArrayBuffer,
  getSafeFilename,
} from '../../utils';
import {
  downloadBlobAsFile,
  getDocumentFormatLabel,
} from '../../utils/download';
import { MBadge, MButton, MFlex, MIcon, MSpinner, MText } from '../Monetize';

interface QuoteDownloadMenuProps {
  quote: QuoteWithPdfFields;
  /** 'button' renders a standalone Download button; 'menuItem' renders a row meant to be embedded inside another kebab Menu */
  variant?: 'button' | 'menuItem';
}

/**
 * Keeps a click from reaching an ancestor's onClick: the table row a
 * variant="menuItem" kebab menu sits in, or, on a picker trigger, the
 * Download list's own onClick, which would close the list before it opens.
 */
const stopPropagation = (event: MouseEvent) => event.stopPropagation();

/**
 * End-aligned under the button when the PDF / Word picker is offered: the
 * button sits near the right edge of the page, and a start-aligned list would
 * leave the fly-out no room to open on the right (floating-ui still flips it
 * left when the viewport is too narrow).
 */
const DOCX_PICKER_POSITIONING = { placement: 'bottom-end' } as const;
const FORMAT_PICKER_POSITIONING = { placement: 'right-start' } as const;

interface DownloadMenuItemProps {
  label: string;
  format: string;
  onClick: () => void;
  isDisabled: boolean;
}

const DownloadMenuItem = ({
  label,
  format,
  onClick,
  isDisabled,
}: DownloadMenuItemProps) => (
  <Menu.Item onClick={onClick} disabled={isDisabled} value={label}>
    <MFlex alignItems="center" justifyContent="space-between" w="100%">
      <MText color="inherit" truncate mr={2}>
        {label}
      </MText>
      <MBadge fontSize="0.8em" variant="subtle" ml="auto" flexShrink={0}>
        {format}
      </MBadge>
    </MFlex>
  </Menu.Item>
);

interface DownloadFormatMenuItemProps {
  label: string;
  /** Prefix for the row's `data-testid`s, e.g. 'full-proposal' */
  testIdPrefix: string;
  onDownloadPdf: () => void;
  onDownloadDocx: () => void;
  isDisabled: boolean;
}

/**
 * Download row that opens a nested PDF / Word picker, for the generated
 * documents the backend can render in either format. `unmountOnExit` keeps
 * only the open submenu mounted, so the format options never collide with the
 * other row's.
 *
 * As a native submenu, the picker opens on hover and stays open while the
 * pointer crosses into the fly-out; click and ArrowRight open it too.
 *
 * Picking a format closes only the picker. Zag's `closeOnSelect` would close
 * the whole menu tree up to the root, so it's off here and the picker's own
 * open state is closed from `onSelect` instead, keeping the Download menu open.
 */
const DownloadFormatMenuItem = ({
  label,
  testIdPrefix,
  onDownloadPdf,
  onDownloadDocx,
  isDisabled,
}: DownloadFormatMenuItemProps) => {
  const { open, onOpen, onClose } = useDisclosure();

  return (
    <Menu.Root
      lazyMount
      unmountOnExit
      closeOnSelect={false}
      open={open}
      onOpenChange={({ open: nextOpen }) => (nextOpen ? onOpen() : onClose())}
      onSelect={onClose}
      positioning={FORMAT_PICKER_POSITIONING}
    >
      <Menu.TriggerItem
        data-testid={`${testIdPrefix}-download-format-btn`}
        data-disabled={isDisabled ? '' : undefined}
        aria-disabled={isDisabled || undefined}
        onClick={stopPropagation}
      >
        <MFlex alignItems="center" justifyContent="space-between" w="100%">
          <MText color="inherit" truncate mr={2}>
            {label}
          </MText>
          {/* No format badge here: the row opens a picker rather than
            downloading a format, so a "PDF" badge would misdescribe it. */}
          <MIcon boxSize={4} as={MdChevronRight} flexShrink={0} />
        </MFlex>
      </Menu.TriggerItem>
      <Portal>
        <Menu.Positioner>
          <Menu.Content>
            <Menu.Item
              value="pdf"
              onClick={onDownloadPdf}
              data-testid={`${testIdPrefix}-download-pdf-btn`}
            >
              <MText color="inherit">PDF</MText>
            </Menu.Item>
            <Menu.Item
              value="docx"
              onClick={onDownloadDocx}
              data-testid={`${testIdPrefix}-download-docx-btn`}
            >
              <MText color="inherit">Word (.docx)</MText>
            </Menu.Item>
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
};

export const QuoteDownloadMenu = ({
  quote,
  variant = 'button',
}: QuoteDownloadMenuProps) => {
  const { enableQuoteDocumentManagement, enableDocxQuoteDownload } = useFlags();
  const [isDownloading, setIsDownloading] = useState(false);
  const { open, onOpen, onClose } = useDisclosure();
  const quoteId = quote.id ?? '';
  const quoteFileName = getSafeFilename(quote.description || 'Quote');

  const { data: attachedDocuments = [] } = useGetAttachedQuoteDocuments(
    quoteId,
    {
      enabled: !!quoteId && enableQuoteDocumentManagement,
    },
  );

  const withDownloadHandling = (download: () => Promise<void>) => async () => {
    try {
      setIsDownloading(true);
      await download();
    } catch (err) {
      handleApiErrorToast(err);
    } finally {
      setIsDownloading(false);
    }
  };

  const getQuoteContent = async () => ({
    content: await doPrintQuoteToPdf(quoteId),
    filename: `${quoteFileName}.pdf`,
  });

  const getQuoteOnlyContent = async () => ({
    content: await doPrintQuoteToPdf(quoteId, true, true),
    filename: `${quoteFileName} - Quote.pdf`,
  });

  const getDocumentContent = async (
    documentConfigId: string,
    mediaType: string,
  ) => {
    const { storageObject } = await doGetQuoteDocumentConfigDetails(
      quoteId,
      documentConfigId,
      true,
      mediaType,
    );
    if (!storageObject.contentBase64Encoded) {
      return null;
    }
    return {
      content: base64ToArrayBuffer(storageObject.contentBase64Encoded),
      filename: storageObject.filename,
    };
  };

  const getLegacyStorageContent = async (storageId: string) => {
    const storage = await doGetQuoteStorageById(quoteId, storageId, {
      attachContent: true,
    });
    if (!storage.contentBase64Encoded) {
      return null;
    }
    return {
      content: base64ToArrayBuffer(storage.contentBase64Encoded),
      filename: storage.filename,
    };
  };

  // The backend refuses a DOCX render once a quote is processed or has a
  // signed document, so the format picker is only offered before then.
  const canDownloadAsDocx =
    enableDocxQuoteDownload &&
    quote.status !== QuoteStatusEnum.PROCESSED &&
    !quote.signedDocumentStorageId;

  const { documentLocation, sowDocumentStorageId } = quote;

  const rawDocuments = enableQuoteDocumentManagement
    ? attachedDocuments.map((doc) => ({
        key: doc.id,
        label: doc.quoteDocumentType.label,
        format: getDocumentFormatLabel(doc.storageObject.mediaType),
        getContent: () =>
          getDocumentContent(doc.id, doc.storageObject.mediaType),
      }))
    : [
        ...(documentLocation
          ? [
              {
                key: 'msa',
                label: 'MSA',
                format: getDocumentFormatLabel(),
                getContent: () => getLegacyStorageContent(documentLocation),
              },
            ]
          : []),
        ...(sowDocumentStorageId
          ? [
              {
                key: 'sow',
                label: 'SOW',
                format: getDocumentFormatLabel(),
                getContent: () => getLegacyStorageContent(sowDocumentStorageId),
              },
            ]
          : []),
      ];

  const documentItems = rawDocuments.map(
    ({ key, label, format, getContent }) => ({
      key,
      label,
      format,
      onClick: withDownloadHandling(async () => {
        const result = await getContent();
        if (result) {
          downloadBlobAsFile(result.content, result.filename);
        }
      }),
    }),
  );

  const downloadQuoteOnly = withDownloadHandling(async () => {
    const content = await doPrintQuoteToPdf(quoteId, true, true);
    downloadBlobAsFile(content, `${quoteFileName}.pdf`);
  });

  const getFullProposalContent = async () => {
    if (quote.signedDocumentStorageId) {
      const storage = await doGetQuoteStorageById(
        quoteId,
        quote.signedDocumentStorageId,
        { attachContent: true },
      );
      if (!storage.contentBase64Encoded) {
        return null;
      }
      return {
        content: base64ToArrayBuffer(storage.contentBase64Encoded),
        filename: `${quoteFileName}.pdf`,
      };
    }
    return getQuoteContent();
  };

  const downloadFullProposal = withDownloadHandling(async () => {
    const result = await getFullProposalContent();
    if (result) {
      downloadBlobAsFile(result.content, result.filename);
    }
  });

  const downloadFullProposalDocx = withDownloadHandling(async () => {
    const content = await doPrintQuoteToDocx(quoteId);
    downloadBlobAsFile(content, `${quoteFileName}.docx`, MimeTypeEnum.DOCX);
  });

  const downloadQuoteOnlyDocx = withDownloadHandling(async () => {
    const content = await doPrintQuoteToDocx(quoteId, true);
    downloadBlobAsFile(content, `${quoteFileName}.docx`, MimeTypeEnum.DOCX);
  });

  const downloadAllFiles = withDownloadHandling(async () => {
    const zip = new JSZip();
    const usedZipFilenames = new Set<string>();
    const [fullProposalContent, quoteOnlyContent] = await Promise.all([
      getFullProposalContent(),
      getQuoteOnlyContent(),
    ]);
    if (fullProposalContent) {
      addUniqueZipFile(
        zip,
        usedZipFilenames,
        fullProposalContent.filename,
        fullProposalContent.content,
      );
    }
    addUniqueZipFile(
      zip,
      usedZipFilenames,
      quoteOnlyContent.filename,
      quoteOnlyContent.content,
    );

    const documentContents = await Promise.all(
      rawDocuments.map(({ getContent }) => getContent()),
    );
    documentContents.forEach((result) => {
      if (result) {
        addUniqueZipFile(
          zip,
          usedZipFilenames,
          result.filename,
          result.content,
        );
      }
    });

    const zipContent = await zip.generateAsync({ type: 'arraybuffer' });
    downloadBlobAsFile(zipContent, `${quoteFileName}.zip`, MimeTypeEnum.ZIP);
  });

  const menuList = (
    <Portal>
      <Menu.Positioner>
        <Menu.Content
          onClick={(event) => {
            // Stop the click from bubbling to an ancestor row's own onClick
            // when this menu renders via variant="menuItem", and close this
            // list on any pick (format picks bubble up here too) while the
            // host kebab menu stays open: `closeOnSelect` is off, since Zag
            // would close the whole tree up to that root. A no-op for the
            // button variant, whose uncontrolled root stays open.
            stopPropagation(event);
            onClose();
          }}
        >
          {canDownloadAsDocx ? (
            <DownloadFormatMenuItem
              label="Full Proposal"
              testIdPrefix="full-proposal"
              onDownloadPdf={downloadFullProposal}
              onDownloadDocx={downloadFullProposalDocx}
              isDisabled={isDownloading}
            />
          ) : (
            <DownloadMenuItem
              label={
                quote.signedDocumentStorageId
                  ? 'Finalized Quote'
                  : 'Full Proposal'
              }
              format={getDocumentFormatLabel()}
              onClick={downloadFullProposal}
              isDisabled={isDownloading}
            />
          )}
          <Menu.Separator />
          {canDownloadAsDocx ? (
            <DownloadFormatMenuItem
              label="Quote"
              testIdPrefix="quote"
              onDownloadPdf={downloadQuoteOnly}
              onDownloadDocx={downloadQuoteOnlyDocx}
              isDisabled={isDownloading}
            />
          ) : (
            <DownloadMenuItem
              label="Quote"
              format={getDocumentFormatLabel()}
              onClick={downloadQuoteOnly}
              isDisabled={isDownloading}
            />
          )}
          {documentItems.map(({ key, label, format, onClick }) => (
            <DownloadMenuItem
              key={key}
              label={label}
              format={format}
              onClick={onClick}
              isDisabled={isDownloading}
            />
          ))}
          {documentItems.length > 0 && <Menu.Separator />}
          <DownloadMenuItem
            label="All Files"
            format="ZIP"
            onClick={downloadAllFiles}
            isDisabled={isDownloading}
          />
        </Menu.Content>
      </Menu.Positioner>
    </Portal>
  );

  if (variant === 'menuItem') {
    return (
      <Menu.Root
        lazyMount
        unmountOnExit
        closeOnSelect={false}
        open={open}
        onOpenChange={({ open: nextOpen }) => (nextOpen ? onOpen() : onClose())}
        positioning={{
          placement: 'right-start',
        }}
      >
        {/* `unstyled` — the menu recipe's trigger styles would otherwise
         * override this item's own color, leaving it darker than its sibling
         * items. v2's `MenuButton as={MenuItem}` never applied them. */}
        <Menu.Trigger asChild unstyled>
          <Menu.Item
            color="tPurple.dark"
            display="flex"
            alignItems="center"
            data-testid="data-table-actions-Download"
            disabled={isDownloading}
            onClick={(e) => e.stopPropagation()}
            value="item-1"
          >
            <MFlex alignItems="center" w="100%">
              <MText color="inherit">Download</MText>
              {isDownloading ? (
                <MSpinner size="xs" ml={2} aria-label="Loading" />
              ) : (
                <MIcon boxSize={4} as={MdChevronRight} ml={2} />
              )}
            </MFlex>
          </Menu.Item>
        </Menu.Trigger>
        {menuList}
      </Menu.Root>
    );
  }

  return (
    // Without the picker the menu keeps Chakra's default placement.
    <Menu.Root
      lazyMount
      unmountOnExit
      closeOnSelect={false}
      positioning={canDownloadAsDocx ? DOCX_PICKER_POSITIONING : undefined}
    >
      {/* `unstyled` — the menu recipe's trigger styles (plain text color and
       * weight, its own hover) would otherwise override the tertiary variant
       * on the button. v2's `MenuButton as={MButton}` never applied them. */}
      <Menu.Trigger asChild unstyled>
        <MButton variant="tertiary" size="sm" isLoading={isDownloading}>
          <MIcon mr={0} boxSize={5} as={MdOutlineFileDownload} />
          Download
        </MButton>
      </Menu.Trigger>
      {menuList}
    </Menu.Root>
  );
};
